import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown, User } from 'lucide-react';
import { useUserAuth } from '../context/UserAuthContext';

const LOGO_URL = "/liquicent-logo.png";

const HeaderNew = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [workflowsOpen, setWorkflowsOpen] = useState(false);
  const [developersOpen, setDevelopersOpen] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useUserAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navLinkClass = (path) => `
    text-sm font-medium transition-colors duration-200
    ${isActive(path) ? 'text-[#1A3A8F]' : 'text-[#475569] hover:text-[#1A3A8F]'}
  `;

  const dropdownItems = {
    workflows: [
      { name: 'Workflow Simulator', path: '/workflow-simulator' },
      { name: 'PR Governance', path: '/workflows/pr-governance' },
      { name: 'Security & Compliance', path: '/workflows/security-compliance' },
      { name: 'Customer Support', path: '/workflows/customer-support' },
      { name: 'Sales Deal Desk', path: '/workflows/sales-deal-desk' },
      { name: 'Vendor Onboarding', path: '/workflows/vendor-onboarding' },
    ],
    developers: [
      { name: 'Overview', path: '/developers' },
      { name: 'APIs', path: '/developers/apis' },
      { name: 'Agent SDK', path: '/developers/agent-sdk' },
      { name: 'Publish Agent', path: '/developers/publish-agent' },
    ],
    research: [
      { name: 'Unified Transaction Graph', path: '/research/unified-transaction-graph' },
      { name: 'DecisionTrace', path: '/research/decisiontrace' },
      { name: 'WorkRank', path: '/research/workrank' },
      { name: 'SocioTRank', path: '/research/sociotrank' },
    ],
  };

  const DropdownMenu = ({ items, isOpen, onClose }) => (
    <div 
      className={`absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-[#E5E7EB] py-2 z-50 transition-all duration-200 ${
        isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
      }`}
      onMouseLeave={onClose}
    >
      {items.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className="block px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F6F7FB] hover:text-[#1A3A8F] transition-colors"
          onClick={() => {
            onClose();
            setMobileMenuOpen(false);
          }}
        >
          {item.name}
        </Link>
      ))}
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-[#E5E7EB]'
          : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <img 
              src={LOGO_URL} 
              alt="Liquicent" 
              className="w-9 h-9 object-contain rounded-lg"
            />
            <span className="text-xl font-semibold text-[#0F172A] tracking-tight">Liquicent</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link to="/" className={`px-4 py-2 ${navLinkClass('/')}`}>
              Home
            </Link>
            
            {/* Workflows Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setWorkflowsOpen(true)}
              onMouseLeave={() => setWorkflowsOpen(false)}
            >
              <button className={`px-4 py-2 flex items-center gap-1 ${navLinkClass('/workflows')}`}>
                Workflows
                <ChevronDown className={`w-4 h-4 transition-transform ${workflowsOpen ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu 
                items={dropdownItems.workflows} 
                isOpen={workflowsOpen}
                onClose={() => setWorkflowsOpen(false)}
              />
            </div>

            <Link to="/agents" className={`px-4 py-2 ${navLinkClass('/agents')}`}>
              Agents Registry
            </Link>

            {/* Developers Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setDevelopersOpen(true)}
              onMouseLeave={() => setDevelopersOpen(false)}
            >
              <button className={`px-4 py-2 flex items-center gap-1 ${navLinkClass('/developers')}`}>
                Developers
                <ChevronDown className={`w-4 h-4 transition-transform ${developersOpen ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu 
                items={dropdownItems.developers} 
                isOpen={developersOpen}
                onClose={() => setDevelopersOpen(false)}
              />
            </div>

            <Link to="/platform-economics" className={`px-4 py-2 ${navLinkClass('/platform-economics')}`}>
              Platform Economics
            </Link>

            {/* Research Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setResearchOpen(true)}
              onMouseLeave={() => setResearchOpen(false)}
            >
              <button className={`px-4 py-2 flex items-center gap-1 ${navLinkClass('/research')}`}>
                Research
                <ChevronDown className={`w-4 h-4 transition-transform ${researchOpen ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu 
                items={dropdownItems.research} 
                isOpen={researchOpen}
                onClose={() => setResearchOpen(false)}
              />
            </div>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <Button
                onClick={() => navigate('/user/dashboard')}
                variant="ghost"
                className="text-[#475569] hover:text-[#1A3A8F] hover:bg-[#F6F7FB]"
              >
                <User className="w-4 h-4 mr-2" />
                {user?.full_name?.split(' ')[0] || 'Dashboard'}
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/user/login')}
                variant="ghost"
                className="text-[#475569] hover:text-[#1A3A8F] hover:bg-[#F6F7FB]"
              >
                Sign In
              </Button>
            )}
            <Button
              onClick={() => navigate('/request-demo')}
              className="bg-[#1A3A8F] hover:bg-[#142D70] text-white px-5 shadow-sm hover:shadow-md transition-all"
            >
              Request Demo
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#475569] hover:text-[#1A3A8F]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-[#E5E7EB]">
          <nav className="flex flex-col px-6 py-4 space-y-1">
            <Link
              to="/"
              className="py-3 text-[#475569] hover:text-[#1A3A8F] font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            
            {/* Mobile Workflows */}
            <div className="py-2">
              <span className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Workflows</span>
              <div className="mt-2 space-y-1 pl-3">
                {dropdownItems.workflows.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block py-2 text-sm text-[#475569] hover:text-[#1A3A8F]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/agents"
              className="py-3 text-[#475569] hover:text-[#1A3A8F] font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Agents Registry
            </Link>

            {/* Mobile Developers */}
            <div className="py-2">
              <span className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Developers</span>
              <div className="mt-2 space-y-1 pl-3">
                {dropdownItems.developers.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block py-2 text-sm text-[#475569] hover:text-[#1A3A8F]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/platform-economics"
              className="py-3 text-[#475569] hover:text-[#1A3A8F] font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Platform Economics
            </Link>

            {/* Mobile Research */}
            <div className="py-2">
              <span className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Research</span>
              <div className="mt-2 space-y-1 pl-3">
                {dropdownItems.research.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block py-2 text-sm text-[#475569] hover:text-[#1A3A8F]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="pt-4 mt-4 border-t border-[#E5E7EB] space-y-3">
              {!isAuthenticated && (
                <Button
                  onClick={() => {
                    navigate('/user/login');
                    setMobileMenuOpen(false);
                  }}
                  variant="outline"
                  className="w-full border-[#1A3A8F] text-[#1A3A8F]"
                >
                  Sign In
                </Button>
              )}
              <Button
                onClick={() => {
                  navigate('/request-demo');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-[#1A3A8F] hover:bg-[#142D70] text-white"
              >
                Request Demo
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default HeaderNew;
